import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    LineChart,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";

// A small palette reused for series / pie slices.
const kColors = [
  "#2563eb",
  "#f97316",
  "#16a34a",
  "#dc2626",
  "#9333ea",
  "#0891b2",
  "#ca8a04",
  "#db2777",
];

// Strips out the label key so the remaining keys are treated as numeric series.
function seriesKeys(data) {
  const keys = new Set();
  data.forEach((row) => {
    Object.keys(row).forEach((k) => {
      if (k !== "label" && k !== "name" && typeof row[k] === "number") {
        keys.add(k);
      }
    });
  });
  return Array.from(keys);
}

// Parses a fenced code block's text into a chart spec, or returns null if it is
// not one. Accepts ```chart blocks (any/no language tag) whose body is a JSON
// object shaped like { type, data: [...] }. This lets us still draw a chart
// even when the model tags the block as ```json or leaves it untagged.
export function parseChartSpec(text) {
  if (typeof text !== "string") {
    return null;
  }
  const trimmed = text.trim();
  if (!trimmed.startsWith("{") || !trimmed.includes('"data"')) {
    return null;
  }
  let parsed;
  try {
    parsed = JSON.parse(trimmed);
  } catch (e) {
    return null;
  }
  if (!parsed || !Array.isArray(parsed.data) || parsed.data.length === 0) {
    return null;
  }
  const type = String(parsed.type || "bar").toLowerCase();
  if (!["bar", "line", "pie"].includes(type)) {
    return null;
  }
  return parsed;
}

// Given the hast `node` of a <pre> element produced by react-markdown for a
// fenced code block, returns { text } when the block is a chart (either tagged
// ```chart or whose body is chart JSON), otherwise null. Used by a `pre`
// renderer so the chart is not wrapped in an invalid <pre><div> nesting.
export function chartFromPreNode(node) {
  const code = node?.children?.find((c) => c.tagName === "code");
  if (!code) {
    return null;
  }
  const text = (code.children || [])
    .map((c) => (typeof c.value === "string" ? c.value : ""))
    .join("")
    .replace(/\n$/, "");
  const classes = code.properties?.className;
  const isChartTag = Array.isArray(classes)
    ? classes.includes("language-chart")
    : /language-chart/.test(String(classes || ""));
  if (isChartTag || parseChartSpec(text)) {
    return { text };
  }
  return null;
}

// Recursively collects the visible text of a hast node.
function nodeText(node) {
  if (!node) {
    return "";
  }
  if (typeof node.value === "string") {
    return node.value;
  }
  return (node.children || []).map(nodeText).join("");
}

// Parses a numeric string like "62%", "1,234", "-5", "3.2" into a Number, or
// null when the cell is not a single number (e.g. a label or "n/a").
function toNumber(text) {
  if (typeof text !== "string") {
    return null;
  }
  const cleaned = text.replace(/[%,\s]/g, "").trim();
  if (cleaned === "" || !/^-?\d*\.?\d+$/.test(cleaned)) {
    return null;
  }
  const n = Number(cleaned);
  return isNaN(n) ? null : n;
}

// Finds all <tr> rows under a hast table node, split into header rows (inside
// <thead>) and body rows (inside <tbody>, or direct children).
function tableRows(node) {
  const head = [];
  const body = [];
  const walk = (n, inHead) => {
    if (!n || !n.children) {
      return;
    }
    n.children.forEach((c) => {
      if (c.tagName === "thead") {
        walk(c, true);
      } else if (c.tagName === "tbody") {
        walk(c, false);
      } else if (c.tagName === "tr") {
        (inHead ? head : body).push(c);
      }
    });
  };
  walk(node, false);
  return { head, body };
}

function cellsOf(tr) {
  return (tr.children || []).filter(
    (c) => c.tagName === "th" || c.tagName === "td"
  );
}

// Converts the hast `node` of a Markdown table into a bar-chart spec when the
// table is numeric (a label column plus one or more numeric columns), otherwise
// returns null so the table renders normally. The first column is used as the
// category (label); every other column that is numeric across the rows becomes
// a series.
export function chartSpecFromTableNode(node) {
  const { head, body } = tableRows(node);
  if (head.length === 0 || body.length === 0) {
    return null;
  }
  const headers = cellsOf(head[head.length - 1]).map((c) => nodeText(c).trim());
  if (headers.length < 2) {
    return null;
  }

  // Determine which non-first columns are numeric across all body rows.
  const numericCols = [];
  for (let col = 1; col < headers.length; col++) {
    let numericCount = 0;
    let total = 0;
    body.forEach((tr) => {
      const cells = cellsOf(tr);
      if (cells[col] === undefined) {
        return;
      }
      total += 1;
      if (toNumber(nodeText(cells[col])) !== null) {
        numericCount += 1;
      }
    });
    // Treat the column as a numeric series if most of its cells are numbers.
    if (total > 0 && numericCount >= Math.ceil(total / 2)) {
      numericCols.push(col);
    }
  }
  if (numericCols.length === 0) {
    return null;
  }

  const data = body
    .map((tr) => {
      const cells = cellsOf(tr);
      const label = nodeText(cells[0] || {}).trim();
      if (!label) {
        return null;
      }
      const row = { label };
      numericCols.forEach((col) => {
        const n = toNumber(nodeText(cells[col] || {}));
        if (n !== null) {
          row[headers[col] || `Series ${col}`] = n;
        }
      });
      // Skip rows that have no numeric values at all.
      const hasValue = numericCols.some(
        (col) => row[headers[col] || `Series ${col}`] !== undefined
      );
      return hasValue ? row : null;
    })
    .filter(Boolean);

  if (data.length === 0) {
    return null;
  }
  return { type: "bar", title: headers[0] || undefined, data };
}

// Renders a chart from a spec emitted by the AI inside a ```chart code block.
// The spec is JSON: { type: "bar"|"line"|"pie", title?, data: [...] }.
// For bar/line each data row is { label, <series>: number, ... }.
// For pie each row is { label, value }.
function AIReportChart({ spec }) {
  const parsed = typeof spec === "string" ? parseChartSpec(spec) : spec;
  if (!parsed) {
    // Not a valid chart spec — show the raw block so nothing is silently lost.
    return (
      <pre className="my-2 overflow-x-auto rounded bg-base-200 p-2 text-[10px]">
        {String(spec)}
      </pre>
    );
  }

  const data = Array.isArray(parsed?.data) ? parsed.data : [];
  if (data.length === 0) {
    return null;
  }
  // Normalise the category key onto `label`.
  const rows = data.map((row) => ({
    label: row.label != null ? row.label : row.name,
    ...row,
  }));
  const type = (parsed.type || "bar").toLowerCase();
  const title = parsed.title;

  let chart;
  if (type === "pie") {
    chart = (
      <PieChart>
        <Pie
          data={rows}
          dataKey="value"
          nameKey="label"
          cx="50%"
          cy="50%"
          outerRadius={70}
          isAnimationActive={false}
          label={(d) => `${d.label}: ${d.value}`}
        >
          {rows.map((entry, i) => (
            <Cell key={i} fill={kColors[i % kColors.length]} />
          ))}
        </Pie>
        <Tooltip />
      </PieChart>
    );
  } else {
    const keys = seriesKeys(rows);
    const series =
      keys.length > 0 ? keys : ["value"]; // single implicit series fallback
    if (type === "line") {
      chart = (
        <LineChart data={rows} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} />
          <Tooltip />
          {series.length > 1 && <Legend wrapperStyle={{ fontSize: 10 }} />}
          {series.map((key, i) => (
            <Line
              key={key}
              type="monotone"
              dataKey={key}
              stroke={kColors[i % kColors.length]}
              strokeWidth={2}
              dot={false}
              isAnimationActive={false}
            />
          ))}
        </LineChart>
      );
    } else {
      chart = (
        <BarChart data={rows} margin={{ top: 8, right: 12, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="label" tick={{ fontSize: 10 }} />
          <YAxis tick={{ fontSize: 10 }} />
          <Tooltip />
          {series.length > 1 && <Legend wrapperStyle={{ fontSize: 10 }} />}
          {series.map((key, i) => (
            <Bar
              key={key}
              dataKey={key}
              fill={kColors[i % kColors.length]}
              isAnimationActive={false}
            />
          ))}
        </BarChart>
      );
    }
  }

  return (
    <div className="my-3 break-inside-avoid rounded-md border border-base-300 bg-base-100 p-2">
      {title && (
        <div className="mb-1 text-center text-[11px] font-semibold">{title}</div>
      )}
      <ResponsiveContainer width="100%" height={200}>
        {chart}
      </ResponsiveContainer>
    </div>
  );
}

export default AIReportChart;
